import { MgmtApi } from '@/customApiClients/mgmtApiClient/Api';

// Fixed filename for every auto-created scheduled-job memory store — the
// user never picks a path, so one predictable name is enough (mirrors the
// manual flow's default of one file per store).
const AUTO_MEMORY_PATH = '/memory.md';

/**
 * Creates a memory store and its one fixed memory file behind the scenes for
 * a scheduled job's "persistent memory" toggle. The user only sees yes/no —
 * never a store name or file path — so this names the store after the job
 * itself, purely so it's still recognizable on the Anthropic side if anyone
 * needs to debug it there.
 */
export async function createAutoMemoryStore(
    jobName: string,
    mgmtApi: MgmtApi<unknown>
): Promise<{ memoryStoreId: string; memoryPath: string }> {
    // 1. Create the store on Anthropic's side (only the chat-server backend
    // talks to Anthropic directly).
    const storeRes = await fetch('/api/memory-stores', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: jobName || 'Scheduled Job Memory' })
    });
    if (!storeRes.ok) {
        throw new Error('Failed to create memory store');
    }
    const createdStore: { id: string; name: string } = await storeRes.json();

    // 2. Register it against this org in mgmt-API, so future listing is a
    // fast, org-scoped DB query instead of fetching every store visible to
    // the API key and filtering client-side.
    await mgmtApi.api.v1ClaudeMemoryStoresCreate({
        anthropicMemoryStoreId: createdStore.id,
        displayName: createdStore.name
    });

    // 3. Pre-create the one fixed memory file so the agent can read it
    // directly on its very first run instead of needing check-then-create
    // logic in the prompt.
    await fetch(`/api/memory-stores/${createdStore.id}/memories`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ path: AUTO_MEMORY_PATH })
    });

    return { memoryStoreId: createdStore.id, memoryPath: AUTO_MEMORY_PATH };
}
